/**
 * DogLivePreview — podgląd na żywo wizytówki psa (prawa kolumna kreatora psa/suki/reproduktora).
 * Styl spójny z LivePreview Kreatora Hodowli (KROKI | FORMULARZ | PODGLĄD NA ŻYWO).
 */
import { useState } from 'react';
import { Monitor, Smartphone, Heart, ShieldCheck, Award, PawPrint, Globe, Search, Sparkles, Check } from 'lucide-react';
import { TITLE_OPTIONS, SHOW_RESULT_OPTIONS } from '@/creators/schemas/dog.profile.schema';

// Mapowanie ID → czytelna nazwa (np. 'ch_pl' → 'Champion Polski (Ch.PL)').
const TITLE_LABELS = Object.fromEntries([...TITLE_OPTIONS, ...SHOW_RESULT_OPTIONS].map((o) => [o.id, o.label]));
const titleLabel = (id) => TITLE_LABELS[id] || id;

const GENDER = {
  female: { label: 'Suka', className: 'bg-pink-50 text-pink-600' },
  male: { label: 'Pies / Reproduktor', className: 'bg-blue-50 text-blue-600' },
};
const ROLE = {
  breeding: 'Hodowlany',
  stud: 'Reproduktor',
  pet: 'Towarzyszący',
  retired: 'Emeryt',
};

export default function DogLivePreview({ form }) {
  const [mobile, setMobile] = useState(false);
  const g = GENDER[form.gender] || GENDER.female;
  const parents = [form.dam_name, form.sire_name].filter(Boolean).join(' × ');
  // Teksty kontekstowe do płci (suka/reproduktor).
  const subjGen = form.gender === 'male' ? 'reproduktora' : 'suki'; // dopełniacz: „Imię rodowe suki"
  const subjAcc = form.gender === 'male' ? 'reproduktora' : 'sukę';  // biernik: „Zapytaj o sukę"

  return (
    <div data-testid="dog-live-preview">
      {/* Pasek statusu podglądu */}
      <div className="mb-3 px-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs uppercase tracking-wider font-semibold text-gray-500 truncate">
              Twoja wizytówka · podgląd na żywo
            </span>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold ring-1 ring-emerald-200/60">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
            </span>
          </div>
          <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
            <button type="button" onClick={() => setMobile(false)} className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${!mobile ? 'bg-white shadow-sm text-orange-600' : 'text-gray-500'}`} aria-label="Desktop">
              <Monitor className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => setMobile(true)} className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${mobile ? 'bg-white shadow-sm text-orange-600' : 'text-gray-500'}`} aria-label="Mobile">
              <Smartphone className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Ecosystem signals strip — 1:1 z Kreatorem Hodowli */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="dog-live-preview-ecosystem-signals">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 text-[10px] font-medium ring-1 ring-orange-200/50">
            <Globe className="w-3 h-3" /> Widoczne publicznie
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium ring-1 ring-blue-200/50">
            <Search className="w-3 h-3" /> Gotowe pod Google
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-[10px] font-medium ring-1 ring-orange-200/50">
            <Sparkles className="w-3 h-3" /> Indeksowane w wyszukiwarkach
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-medium ring-1 ring-emerald-200/50">
            <Check className="w-3 h-3" /> Mobile-ready
          </span>
        </div>
      </div>

      {/* Karta wizytówki */}
      <div className={`mx-auto bg-white rounded-3xl border border-zinc-200 shadow-[0_18px_50px_-20px_rgba(0,0,0,0.25)] overflow-hidden transition-all ${mobile ? 'max-w-[320px]' : 'w-full'}`}>
        {/* Hero / zdjęcie główne */}
        <div className="relative aspect-[16/10] bg-gradient-to-br from-orange-100 to-zinc-100">
          {form.main_photo ? (
            <img src={form.main_photo} alt={form.name || 'pies'} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-orange-300 gap-2">
              <PawPrint className="w-10 h-10" />
              <span className="text-xs text-zinc-400">Dodaj zdjęcie główne w kroku „Zdjęcia"</span>
            </div>
          )}
          <div className="absolute top-3 left-3 flex gap-1.5">
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${g.className}`}>{g.label}</span>
            {form.role && ROLE[form.role] && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/90 text-zinc-700">{ROLE[form.role]}</span>
            )}
          </div>
        </div>

        {/* Treść */}
        <div className="p-5 space-y-3">
          <div>
            <h3 className="text-xl font-bold text-zinc-900 leading-tight">{form.name || `Imię rodowe ${subjGen}`}</h3>
            <p className="text-sm text-zinc-500">{form.breed || 'Rasa'}{form.color ? ` · ${form.color}` : ''}</p>
          </div>

          {(form.selected_titles?.length > 0 || form.achievements_text) && (
            <div className="flex items-start gap-2 text-sm text-zinc-700">
              <Award className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="line-clamp-2">{form.selected_titles?.length > 0 ? form.selected_titles.map(titleLabel).join(', ') : form.achievements_text}</span>
            </div>
          )}

          {parents && (
            <div className="flex items-start gap-2 text-sm text-zinc-700">
              <Heart className="w-4 h-4 text-pink-400 flex-shrink-0 mt-0.5" />
              <span className="line-clamp-1">Rodzice: {parents}</span>
            </div>
          )}

          {form.selected_health_tests?.length > 0 && (
            <div className="flex items-start gap-2 text-sm text-emerald-700">
              <ShieldCheck className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <span>Badania zdrowotne: {form.selected_health_tests.length}</span>
            </div>
          )}

          {/* Warunki krycia — tylko reproduktor (samiec) */}
          {form.gender === 'male' && (form.stud_price || form.stud_conditions) && (
            <div className="rounded-xl bg-orange-50 border border-orange-100 p-3 space-y-1.5" data-testid="dog-live-preview-stud">
              <p className="text-[11px] uppercase tracking-wider font-bold text-orange-600">Warunki krycia</p>
              {form.stud_price && (
                <p className="text-sm text-zinc-800 font-medium">Cena krycia: {form.stud_price}</p>
              )}
              {form.stud_conditions && (
                <p className="text-xs text-zinc-600 leading-relaxed line-clamp-3">{form.stud_conditions}</p>
              )}
            </div>
          )}

          <p className="text-sm text-zinc-500 leading-relaxed line-clamp-3">
            {form.description || `Tu pojawi się opis — dodasz go w kroku „Opis".`}
          </p>

          <button className="w-full mt-1 h-10 rounded-xl bg-orange-500 text-white text-sm font-semibold">
            {form.gender === 'male' ? 'Zapytaj o krycie' : `Zapytaj o ${subjAcc}`}
          </button>
        </div>

        {/* Reproduktor = niezależna strona promocyjna (Google/Bing/...).
            Suka = pojawia się tylko na stronie hodowli. */}
        {form.gender === 'male' ? (
          <div className="bg-gray-50 border-t border-gray-100 p-3">
            <p className="text-gray-500 text-[10px] text-center mb-1">
              Po opublikowaniu profil będzie widoczny w:
            </p>
            <p className="text-orange-600 text-[10px] text-center font-medium">
              {['Google', 'Bing', 'Mapy', 'Katalog APISAI', 'Facebook', 'Asystenci'].join(' · ')}
            </p>
          </div>
        ) : (
          <div className="bg-gray-50 border-t border-gray-100 p-3">
            <p className="text-gray-500 text-[10px] text-center">
              Pojawi się na <span className="text-orange-600 font-medium">stronie Twojej hodowli</span> (sekcja „Nasze psy")
            </p>
          </div>
        )}
      </div>

      <p className="text-center text-xs text-zinc-400 mt-3">
        {form.gender === 'male'
          ? 'To samodzielna strona reproduktora — możesz ją promować niezależnie od hodowli.'
          : 'Suka jest częścią Twojej hodowli — prezentuje się na stronie hodowli.'}
      </p>
    </div>
  );
}
